/* ══════════════════════════════════════════════
   kimorii — Klaviyo pop-up engine
   theo & amelia Co., Ltd.

   Self-initialising. Reads config from data-* attributes on each
   [data-kpopup] element rendered by sections/popup-email.liquid and:
     • arms its trigger (exit-intent + delay fallback / scroll / timer)
     • caps frequency per-popup and globally via localStorage
     • shows one popup per browser session
     • submits to Klaviyo's client-side subscribe endpoint, routing the
       email to the list id set on that placement
     • identifies the profile to onsite.js for analytics attribution
   No build step, no dependencies.
   ══════════════════════════════════════════════ */
(function () {
  'use strict';

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';
  var SESSION_KEY = 'kpopup_shown_session';
  var GLOBAL_KEY = 'kpopup_global_until';
  var GLOBAL_GRACE_DAYS = 14;   // after any signup, hush every popup this long
  var JOINED_DAYS = 365;        // never re-show a popup whose list they joined
  var DAY_MS = 86400000;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function store(get, key, val) {
    try {
      if (get) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, val);
    } catch (e) { return null; }
  }
  function session(get, key, val) {
    try {
      if (get) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, val);
    } catch (e) { return null; }
  }

  function now() { return new Date().getTime(); }

  function suppressedUntil(key) {
    var raw = store(true, key);
    var until = raw ? parseInt(raw, 10) : 0;
    return until && until > now();
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function isHoverDevice() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function Popup(root) {
    this.root = root;
    this.popupId = root.getAttribute('data-popup-id') || 'default';
    this.listId = root.getAttribute('data-list-id') || '';
    this.company = root.getAttribute('data-company') || '';
    this.trigger = root.getAttribute('data-trigger') || 'exit_intent';
    this.scrollPct = parseInt(root.getAttribute('data-scroll-percent'), 10) || 40;
    this.delay = (parseInt(root.getAttribute('data-delay'), 10) || 8) * 1000;
    this.freqDays = parseInt(root.getAttribute('data-frequency-days'), 10);
    if (isNaN(this.freqDays)) this.freqDays = 14;
    this.source = root.getAttribute('data-source') || 'kimorii popup';

    this.selfKey = 'kpopup_self_' + this.popupId;
    this.shown = false;
    this.timers = [];
    this.boundExit = null;
    this.boundScroll = null;

    this.overlay = root.querySelector('[data-kpopup-overlay]');
    this.card = root.querySelector('.kpopup__card');
    this.form = root.querySelector('.kpopup__form');
    this.emailInput = root.querySelector('input[type="email"]');
    this.nameInput = root.querySelector('input[name="kpopup-name"]');
    this.successEl = root.querySelector('.kpopup__success');
    this.formWrap = root.querySelector('.kpopup__formwrap');
    this.errorEl = root.querySelector('.kpopup__error');
    this.submitBtn = root.querySelector('button[type="submit"]');
    this.lastFocused = null;
  }

  Popup.prototype.eligible = function () {
    if (!this.listId || !this.company) return false;            // not configured
    if (session(true, SESSION_KEY)) return false;               // one popup / session
    if (suppressedUntil(GLOBAL_KEY)) return false;              // post-signup grace
    if (suppressedUntil(this.selfKey)) return false;            // per-popup cap
    return true;
  };

  Popup.prototype.arm = function () {
    if (!this.eligible()) return;
    var self = this;

    if (this.trigger === 'scroll') {
      this.boundScroll = function () { self.onScroll(); };
      window.addEventListener('scroll', this.boundScroll, { passive: true });
      this.onScroll();
    } else if (this.trigger === 'timer') {
      this.timers.push(setTimeout(function () { self.open(); }, this.delay));
    } else { // exit_intent (+ delay fallback)
      if (isHoverDevice()) {
        this.boundExit = function (e) {
          if (e.clientY <= 0 && !e.relatedTarget) self.open();
        };
        document.addEventListener('mouseout', this.boundExit);
      }
      this.timers.push(setTimeout(function () { self.open(); }, this.delay));
    }
  };

  Popup.prototype.onScroll = function () {
    var doc = document.documentElement;
    var scrolled = (window.scrollY || doc.scrollTop);
    var max = (doc.scrollHeight - window.innerHeight);
    if (max <= 0) return;
    if ((scrolled / max) * 100 >= this.scrollPct) this.open();
  };

  Popup.prototype.disarm = function () {
    if (this.boundExit) document.removeEventListener('mouseout', this.boundExit);
    if (this.boundScroll) window.removeEventListener('scroll', this.boundScroll);
    this.timers.forEach(function (t) { clearTimeout(t); });
    this.timers = [];
  };

  Popup.prototype.open = function () {
    if (this.shown || !this.eligible()) return;
    this.shown = true;
    this.disarm();
    session(false, SESSION_KEY, '1');

    this.lastFocused = document.activeElement;
    var self = this;
    this.root.classList.add('is-active');
    // next frame so the entrance transition actually runs (no t=0 flash)
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.root.classList.add('is-open');
        if (self.emailInput && !prefersReducedMotion()) {
          try { self.emailInput.focus({ preventScroll: true }); } catch (e) {}
        }
      });
    });

    this.onKeydown = function (e) {
      if (e.key === 'Escape') self.close('dismiss');
      else if (e.key === 'Tab') self.trapFocus(e);
    };
    document.addEventListener('keydown', this.onKeydown);

    this.root.querySelectorAll('[data-kpopup-close]').forEach(function (el) {
      el.addEventListener('click', function () { self.close('dismiss'); });
    });
    if (this.form) this.form.addEventListener('submit', function (e) { self.submit(e); });
  };

  Popup.prototype.trapFocus = function (e) {
    var f = this.root.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    f = Array.prototype.filter.call(f, function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  Popup.prototype.close = function (reason) {
    var self = this;
    this.root.classList.remove('is-open');
    if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);

    var finish = function () { self.root.classList.remove('is-active'); };
    if (prefersReducedMotion()) finish();
    else this.timers.push(setTimeout(finish, 400));

    if (this.lastFocused && this.lastFocused.focus) {
      try { this.lastFocused.focus({ preventScroll: true }); } catch (e) {}
    }
    // cap on dismiss (not on success — success uses longer windows)
    if (reason === 'dismiss') {
      store(false, this.selfKey, String(now() + this.freqDays * DAY_MS));
    }
  };

  Popup.prototype.setError = function (msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  };

  Popup.prototype.submit = function (e) {
    e.preventDefault();
    if (this.errorEl) this.errorEl.hidden = true;

    var email = (this.emailInput.value || '').trim();
    if (!EMAIL_RE.test(email)) {
      this.setError('Please enter a valid email address.');
      this.emailInput.focus();
      return;
    }

    var self = this;
    var name = this.nameInput ? (this.nameInput.value || '').trim() : '';
    this.submitBtn.disabled = true;
    var originalLabel = this.submitBtn.textContent;
    this.submitBtn.textContent = '…';

    // The client-side subscribe endpoint applies consent from the list's own
    // opt-in setting; a `subscriptions` object is NOT a valid profile field here
    // (that belongs to the server-side API) and returns a 400 if included.
    var profileAttrs = { email: email };
    if (name) profileAttrs.first_name = name;

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: self.source,
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: self.listId } } }
      }
    };

    fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(self.company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (res.status === 202 || (res.status >= 200 && res.status < 300)) {
        self.onSuccess(email, name);
      } else {
        return res.json().then(function (data) {
          var detail = data && data.errors && data.errors[0] && data.errors[0].detail;
          throw new Error(detail || 'subscribe failed');
        }).catch(function () { throw new Error('subscribe failed'); });
      }
    }).catch(function () {
      self.submitBtn.disabled = false;
      self.submitBtn.textContent = originalLabel;
      self.setError('Something went wrong. Please try again in a moment.');
    });
  };

  Popup.prototype.onSuccess = function (email, name) {
    // attribute to onsite.js if present
    try {
      if (window.klaviyo && window.klaviyo.push) {
        var id = { $email: email };
        if (name) id.$first_name = name;
        window.klaviyo.push(['identify', id]);
      }
    } catch (e) {}

    // Meta Lead event, for paid-social optimisation. The pixel is installed as a Shopify
    // web pixel and loads asynchronously, so fbq can still be undefined here on a fast
    // signup. Retry briefly, then give up quietly rather than throwing.
    try {
      var leadSource = this.source || 'kimorii popup';
      var leadTries = 0;
      var fireLead = function () {
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: leadSource,
            content_category: 'email signup'
          });
          return;
        }
        if (++leadTries < 20) window.setTimeout(fireLead, 250);
      };
      fireLead();
    } catch (e) {}

    // Publish the same signup on Shopify's analytics bus, so a Customer Events pixel can
    // pick it up later (server-side CAPI, GA4, anything) without another theme change.
    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish('kimorii_lead', { source: this.source || '' });
      }
    } catch (e) {}

    // suppress this popup for a year, and all popups for a short grace
    store(false, this.selfKey, String(now() + JOINED_DAYS * DAY_MS));
    store(false, GLOBAL_KEY, String(now() + GLOBAL_GRACE_DAYS * DAY_MS));

    if (this.formWrap) this.formWrap.hidden = true;
    if (this.successEl) {
      this.successEl.hidden = false;
      var h = this.successEl.querySelector('h3, [tabindex]');
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus(); } catch (e) {} }
    }
  };

  function init() {
    var roots = document.querySelectorAll('[data-kpopup]');
    if (!roots.length) return;
    // arm them all; the session/global guards ensure only one ever opens
    Array.prototype.forEach.call(roots, function (root) {
      new Popup(root).arm();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
